// src/components/learning/SubjectList.tsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getSubjects } from "../../services/learningService";

export interface Subject {
  id: string;
  name: string;
  gradeLevels: number[];
  terms: string[];
}

interface SubjectListProps {
  term: number;
  grade: string;
  onSubjectSelect: (subject: string) => void;
}

const Container = styled.div`
  max-width: 600px;
  margin: 20px auto;
  text-align: center;
  padding: 20px;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  margin-bottom: 20px;
`;

const SubjectsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
`;

const SubjectCard = styled.div`
  background-color: #fff;
  border: 1px solid ${({ theme }) => theme.primary || "#7e22ce"};
  border-radius: 8px;
  padding: 16px 24px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-size: 1rem;
  color: #333;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const SubjectList: React.FC<SubjectListProps> = ({ term, grade, onSubjectSelect }) => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const fetchSubjects = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getSubjects(grade, term);
        // Transform each subject ensuring that the gradeLevels array is correctly assigned.
        const transformedSubjects: Subject[] = data.map((item: any) => ({
          id: item._id || item.id,
          name: item.name,
          gradeLevels: item.gradeLevels || item.gradeLevel, // Use item.gradeLevel directly.
          terms: item.terms,
        }));
  
        const filteredSubjects = transformedSubjects.filter((subj) => {
          return (
            subj.gradeLevels.includes(Number(grade)) &&
            subj.terms.includes(term.toString())
          );
        });
  
        setSubjects(filteredSubjects);
      } catch (err) {
        setError("Failed to fetch subjects. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
  
    fetchSubjects();
  }, [term, grade]);
  

  if (loading) return <p>Loading subjects...</p>;
  if (error) return <p>{error}</p>;
  if (subjects.length === 0)
    return <p>No subjects found for Term {term}, Grade {grade}.</p>;

  return (
    <Container>
      <Title>Subjects for Term {term}, Grade {grade}</Title>
      <SubjectsContainer>
        {subjects.map((subject) => (
          <SubjectCard key={subject.id} onClick={() => onSubjectSelect(subject.name)}>
            {subject.name}
          </SubjectCard>
        ))}
      </SubjectsContainer>
    </Container>
  );
};

export default SubjectList;
